import {ChangeDetectorRef, Component, ElementRef, NgZone, OnDestroy, OnChanges, OnInit, Optional, SimpleChanges, ChangeDetectionStrategy, model, output, viewChild, input, inject} from "@angular/core";
import {FormControl, ReactiveFormsModule} from "@angular/forms";
import {MatIconModule} from "@angular/material/icon";
import {MatProgressSpinnerModule} from "@angular/material/progress-spinner";
import {MatSliderDragEvent, MatSliderModule} from "@angular/material/slider";
import {HypecastAuthenticationService} from "@app/shared/authentication/services/authentication.service";
import {__} from "@app/shared/functions/object.functions";
import {PodcastType} from "@app/shared/models/classes/Podcast";
import {MinuteSecondsPipe} from "@app/shared/pipes/minute-seconds.pipe";
import {ApiUrlService} from "@app/shared/services/api-url.service";
import {Howl} from "howler";
import {interval, NEVER, Subject, Subscription} from "rxjs";
import {distinctUntilChanged, map, skip, switchMap, takeUntil} from "rxjs/operators";

import {AudioPlayerService, QueueItem} from "../audio-player-control/audio-player.service";
import {ContentLocaleService} from "@app/shared/services/content-locale.service";
import {ContentLanguageSwitcherComponent} from "@app/shared/components/content-language-switcher/content-language-switcher.component";
import {FilesService} from "@app/shared/services/files.service";
import {PlaybackPositionService, playbackTrackKey} from "@app/shared/services/playback-position.service";
import {ImageComponent} from "@app/shared/components/image/image.component";
import {TranslateModule} from "@ngx-translate/core";

const SPEED_OPTIONS = [1, 1.25, 1.5, 2, 0.75];

@Component({
    selector: "hypecast-audio-player",
    templateUrl: "./audio-player.component.html",
    styleUrls: ["./audio-player.component.scss"],
    imports: [ReactiveFormsModule, MatIconModule, MatProgressSpinnerModule, MatSliderModule, ImageComponent, MinuteSecondsPipe, TranslateModule, ContentLanguageSwitcherComponent],
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [MinuteSecondsPipe, FilesService]
})
export class AudioPlayerComponent implements OnInit, OnChanges, OnDestroy {
  private readonly contentLocaleService = inject(ContentLocaleService);
  PodcastType = PodcastType;

  sound!: Howl | null;

  duration!: number;

  max: number = 0;

  min: number = 0;

  readonly disabled = model(true);
  readonly isLoading = model(false);

  // Transcript toggle (rendered between speed and volume; the panel itself is
  // owned by the host, e.g. the mini-player bar).
  readonly hasTranscript = input<boolean>(false);
  readonly transcriptOpen = input<boolean>(false);
  readonly transcriptToggle = output<void>();

  stepSize: number = 1;

  seek$: Subject<number> = new Subject<number>();

  isErroneous: boolean = false;

  isPlaying: boolean = false;

  // True while the bar mirrors/controls an external source (inline <video>) rather
  // than its own Howl. Set from AudioPlayerService.externalSource$.
  isExternalSource: boolean = false;

  isPlaying$: Subject<boolean> = new Subject<boolean>();

  isDraggingSlider$: Subject<boolean> = new Subject<boolean>();

  sliderControl: FormControl;
  volumeControl: FormControl;

  sliderValueChangeSubscription!: Subscription;

  currentItem!: QueueItem;

  streamStart: any;
  streamEnd: any;
  secondsStreamed!: number;
  private trackListenInterval: any;
  isListened: boolean = false;

  ngOnInitHasRun: boolean = false;

  playbackRate: number = 1;
  isMuted: boolean = false;
  private previousVolume: number = 100;
  isMobile: boolean = false;

  readonly playClicked = output<boolean>();

  readonly pauseClicked = output<number>();

  readonly endReached = output<number>();

  readonly trackEventSent = output<number>();

  readonly timer = viewChild<ElementRef>("timer");

  readonly slider = viewChild("slider", { read: ElementRef });

  readonly src = input("");

  // Seconds to resume from when the next audio loads. Set when switching
  // video→audio so the bar's fresh Howl continues from the video position
  // instead of restarting at 0. Consumed once in afterLoad, then cleared.
  readonly startPosition = model<number>();

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['src']) {
      return;
    }
    this.isListened = false;
    if (this.ngOnInitHasRun === true) {
      this.loadAudio();
    }
  }

  private _destroy$?: Subject<void>;
  private get destroy$(): Subject<void> {
    return this._destroy$ ??= new Subject<void>();
  }

  constructor(
    private authenticationService: HypecastAuthenticationService,
    private minuteSecondsPipe: MinuteSecondsPipe,
    private apiUrlService: ApiUrlService,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
    private audioPlayerService: AudioPlayerService,
    private filesService: FilesService,
    // Optional: plugin contexts reuse this component without the resume store.
    @Optional() private playbackPositionService?: PlaybackPositionService
  ) {
    this.isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

    this.sliderControl = new FormControl(0);
    this.volumeControl = new FormControl(100);

    this.sliderControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((position: number) => {
        this.seek(position);
      });

    this.volumeControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((volume: number) => {
        if (this.isExternalSource) {
          this.audioPlayerService.sendCommand({ type: 'volume', value: volume / 100 });
          this.isMuted = volume === 0;
          if (volume > 0) {
            this.previousVolume = volume;
          }
          return;
        }
        if (!__.IsNullOrUndefined(this.sound)) {
          this.sound.volume(volume / 100);
          this.isMuted = volume === 0;
          if (volume > 0) {
            this.previousVolume = volume;
          }
        }
      });

    // The bar keeps its own copy of the episode; when the content language changes anywhere
    // (chips on a detail page or on the bar itself) it swaps to that language's recording.
    this.contentLocaleService.preference$
      .pipe(skip(1), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((preference) => {
        if (preference !== "auto") {
          this.audioPlayerService.switchLanguage(preference);
        }
      });

    this.audioPlayerService.isPlaying$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isPlaying: QueueItem | null) => {
        if (!isPlaying) return;
        this.currentItem = isPlaying;

        if (this.isPlaying !== isPlaying.isPlaying) {
          this.isPlaying = this.currentItem.isPlaying;
          this.isPlaying$.next(this.currentItem.isPlaying);

          if (!__.IsNullOrUndefined(this.sound)) {
            if (this.isPlaying === true && this.sound.playing() === false) {
              this.sound.play();
            }
            if (this.isPlaying === false && this.sound.playing() === true) {
              this.sound.pause();
            }
          }
        }
      });

    this.audioPlayerService.seekTo$
      .pipe(takeUntil(this.destroy$))
      .subscribe((position: number) => {
        if (this.sound) {
          this.sound.seek(position);
          this.sliderControl.setValue(position, { emitEvent: false });
          this.audioPlayerService.updateProgress(position, this.duration || 0);
        }
      });

    // Another player (e.g. the inline detail player) took over — stop our Howl
    // so two audio sources never play at once. owner '' is the miniplayer itself.
    this.audioPlayerService.stopPlayback$
      .pipe(takeUntil(this.destroy$))
      .subscribe((owner: string) => {
        if (owner !== '' && this.sound && this.sound.playing()) {
          this.sound.pause();
        }
      });

    this.audioPlayerService.externalSource$
      .pipe(takeUntil(this.destroy$))
      .subscribe((active: boolean) => {
        this.isExternalSource = active;
        if (active) {
          // No Howl in external mode — don't sit greyed out.
          this.disabled.set(false);
        } else if (__.IsNullOrUndefined(this.sound)) {
          // Reverted to audio with no loaded Howl yet — disable until it loads.
          this.disabled.set(true);
        }
        // markForCheck (not detectChanges): BehaviorSubject replays synchronously
        // when the constructor subscribes, before the view exists. detectChanges
        // would force a render of a half-built template and throw; markForCheck
        // just dirties the path for the next normal CD tick.
        this.cdr.markForCheck();
      });

    this.audioPlayerService.progress$
      .pipe(takeUntil(this.destroy$))
      .subscribe((p) => {
        if (this.isExternalSource) {
          this.duration = p.duration || this.duration;
          this.max = this.duration;
          this.sliderControl.setValue(p.position, { emitEvent: false });
          this.setTimer(p.position);
        }
      });

    this.isPlaying$
      .pipe(
        map((isPlaying: boolean) => {
          return {name: "isPlaying", value: isPlaying};
        }),
        switchMap(($event: { name: string; value: boolean }) => {
          return this.isPlaying === true && !this.isExternalSource && !__.IsNullOrUndefined(this.sound)
            ? interval(200).pipe(
              map(() => {
                return this.sound!.seek() || 0;
              })
            )
            : NEVER;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((position: number) => {
        this.sliderControl.setValue(position, {emitEvent: false});
      });
  }

  get episodeImageUrl(): string {
    const ep = this.currentItem?.episode;
    if (!ep) return '';
    return ep.imageLarge?.fileSource || ep.image?.fileSource || ep.externalImageLink || '';
  }

  ngOnInit() {
    this.ngOnInitHasRun = true;
    this.loadAudio();
  }

  updateMediaSessionMetadata(): void {
    if ('mediaSession' in navigator && this.currentItem?.episode) {
      const artwork = [];

      if (this.currentItem.episode?.imageLarge?.fileSource) {
        artwork.push({src: this.currentItem.episode.imageLarge.fileSource, sizes: '512x512', type: 'image/jpeg'});
      } else if (this.currentItem.episode?.externalImageLink) {
        artwork.push({src: this.currentItem.episode.externalImageLink, sizes: '512x512', type: 'image/jpeg'});
      }

      navigator.mediaSession.metadata = new MediaMetadata({
        title: this.currentItem.episode?.title || '',
        artist: this.currentItem.episode?.season?.podcast?.title || '',
        artwork: artwork
      });

      navigator.mediaSession.setActionHandler('play', () => this.play());
      navigator.mediaSession.setActionHandler('pause', () => this.pause());
      navigator.mediaSession.setActionHandler('seekbackward', () => this.rewind());
      navigator.mediaSession.setActionHandler('seekforward', () => this.forward());

      if ('setPositionState' in navigator.mediaSession) {
        navigator.mediaSession.setPositionState({
          duration: this.duration || 0,
          playbackRate: this.playbackRate,
          position: this.sound ? (this.sound.seek() as number) || 0 : 0
        });
      }
    }
  }

  rewind(): void {
    if (this.isExternalSource) {
      this.audioPlayerService.sendCommand({ type: 'skip', value: -30 });
      return;
    }
    if (!__.IsNullOrUndefined(this.sound)) {
      const currentSeek = (this.sound.seek() as number) || 0;
      const newPosition = Math.max(0, currentSeek - 30);
      this.sound.seek(newPosition);
      this.sliderControl.setValue(newPosition, {emitEvent: false});
      this.setTimer(newPosition);
    }
  }

  forward(): void {
    if (this.isExternalSource) {
      this.audioPlayerService.sendCommand({ type: 'skip', value: 30 });
      return;
    }
    if (!__.IsNullOrUndefined(this.sound)) {
      const currentSeek = (this.sound.seek() as number) || 0;
      const newPosition = Math.min(this.duration || 0, currentSeek + 30);
      this.sound.seek(newPosition);
      this.sliderControl.setValue(newPosition, {emitEvent: false});
      this.setTimer(newPosition);
    }
  }

  toggleSpeed(): void {
    const currentIndex = SPEED_OPTIONS.indexOf(this.playbackRate);
    const nextIndex = (currentIndex + 1) % SPEED_OPTIONS.length;
    this.playbackRate = SPEED_OPTIONS[nextIndex];

    if (this.isExternalSource) {
      this.audioPlayerService.sendCommand({ type: 'rate', value: this.playbackRate });
      return;
    }

    if (!__.IsNullOrUndefined(this.sound)) {
      this.sound.rate(this.playbackRate);
    }

    if ('mediaSession' in navigator && 'setPositionState' in navigator.mediaSession) {
      navigator.mediaSession.setPositionState({
        duration: this.duration || 0,
        playbackRate: this.playbackRate,
        position: this.sound ? (this.sound.seek() as number) || 0 : 0
      });
    }
  }

  toggleMute(): void {
    if (this.isExternalSource) {
      this.isMuted = !this.isMuted;
      this.audioPlayerService.sendCommand({ type: 'mute' });
      if (this.isMuted) {
        this.volumeControl.setValue(0, { emitEvent: false });
      } else {
        this.volumeControl.setValue(this.previousVolume, { emitEvent: false });
      }
      return;
    }
    if (!__.IsNullOrUndefined(this.sound)) {
      this.isMuted = !this.isMuted;
      this.sound.mute(this.isMuted);
      if (this.isMuted) {
        this.volumeControl.setValue(0, {emitEvent: false});
      } else {
        this.volumeControl.setValue(this.previousVolume, {emitEvent: false});
        this.sound.volume(this.previousVolume / 100);
      }
    }
  }

  loadAudio(): void {
    if (!__.IsNullOrUndefined(this.sound)) {
      this.pause();
      this.streamEnd = this.sound.seek();
      this.sound.unload();
      this.sound = null;
    }

    setTimeout(() => {
      if (!__.IsNullOrUndefinedOrEmpty(this.src())) {
        this.isLoading.set(true);

        let src = "";

        if (this.src().startsWith("http")) {
          src = `${this.src()}`;
          this.sound = new Howl({
            src: [src],
            pool: 1,
            preload: true,
            html5: true,

            onloaderror: () => {
              this.disabled.set(true);
              this.duration = 0;
            },
            onplay: () => {
              this.audioPlayerService.play();
              this.audioPlayerService.stopPlayback('');
              requestAnimationFrame(this.step.bind(this));
              this.playClicked.emit(this.isListened);
              this.isListened = true;
              this.streamStart = this.sound!.seek();
              this.isPlaying = true;
              this.isPlaying$.next(true);
              this.currentItem.isPlaying = true;

              if ('mediaSession' in navigator) {
                navigator.mediaSession.playbackState = 'playing';
              }
            },
            onend: () => {
              this.onEnd();
            },
            onpause: () => {
              this.onPause();
            },
            onseek: () => {
              requestAnimationFrame(this.step.bind(this));

              if ('mediaSession' in navigator && 'setPositionState' in navigator.mediaSession) {
                navigator.mediaSession.setPositionState({
                  duration: this.duration || 0,
                  playbackRate: this.playbackRate,
                  position: this.sound ? (this.sound.seek() as number) || 0 : 0
                });
              }
            },
          });
          if (this.sound.state() === "loaded") {
            this.afterLoad();
          }

          this.sound.once("load", () => {
            this.afterLoad();
          });

          this.sound.once("end", () => {
            this.onPause();
            this.sliderControl.setValue(0, {emitEvent: false});
          });

          this.sound.once("loaderror", () => {
            this.isLoading.set(false);
            this.isErroneous = true;
          });

          this.updateMediaSessionMetadata();
        } else {
          this.filesService.getPresignedUrl(this.src())
            .pipe(takeUntil(this.destroy$))
            .subscribe(
              (signedUrl) => {
                this.isLoading.set(false);

                this.sound = new Howl({
                  src: [signedUrl],
                  pool: 1,
                  preload: true,
                  html5: true,

                  onloaderror: () => {
                    this.disabled.set(true);
                    this.duration = 0;
                  },
                  onplay: () => {
                    this.audioPlayerService.play();
                    this.audioPlayerService.stopPlayback('');
                    requestAnimationFrame(this.step.bind(this));
                    this.playClicked.emit(this.isListened);
                    this.isListened = true;
                    this.streamStart = this.sound!.seek();
                    this.isPlaying = true;
                    this.isPlaying$.next(true);
                    this.currentItem.isPlaying = true;

                    if ('mediaSession' in navigator) {
                      navigator.mediaSession.playbackState = 'playing';
                    }

                    this.trackListenInterval = setInterval(() => {
                      this.streamStart = this.sound!.seek();
                      this.trackEventSent.emit(5);
                    }, 5000);
                  },
                  onend: () => {
                    this.onEnd();
                  },
                  onpause: () => {
                    this.onPause();
                  },
                  onseek: () => {
                    requestAnimationFrame(this.step.bind(this));

                    if ('mediaSession' in navigator && 'setPositionState' in navigator.mediaSession) {
                      navigator.mediaSession.setPositionState({
                        duration: this.duration || 0,
                        playbackRate: this.playbackRate,
                        position: this.sound ? (this.sound.seek() as number) || 0 : 0
                      });
                    }
                  },
                });
                if (this.sound.state() === "loaded") {
                  this.afterLoad();
                }

                this.sound.once("load", () => {
                  this.afterLoad();
                });

                this.sound.once("end", () => {
                  this.onPause();
                  this.sliderControl.setValue(0, {emitEvent: false});
                });

                this.sound.once("loaderror", () => {
                  this.isLoading.set(false);
                  this.isErroneous = true;
                });
              },
              (error) => {
                this.isLoading.set(false);
                console.error("Error fetching signed URL:", error);
              }
            );
        }
      } else {
        // No source — e.g. the bar is only reflecting a video episode whose
        // playback lives in the inline <video> element. Nothing to load/play.
        this.isLoading.set(false);
      }
    }, 0);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.persistPosition(true);
    if (this.trackListenInterval) {
      clearInterval(this.trackListenInterval);
      this.trackListenInterval = null;
    }
    if (!__.IsNullOrUndefined(this.sound)) {
      this.sound.stop();
      this.sound.unload();
    }
  }

  afterLoad(): void {
    this.isLoading.set(false);
    this.disabled.set(false);
    this.duration = this.sound!.duration();
    this.max = this.duration;

    // Apply current playback rate to new sound
    if (this.playbackRate !== 1) {
      this.sound!.rate(this.playbackRate);
    }

    // Resume from a requested position (video→audio switch). A html5 Howl
    // overwrites a seek issued synchronously with play() when the underlying
    // <audio> element (re)starts, so seek once playback has actually begun.
    const resumeAt = this.startPosition();
    if (resumeAt && resumeAt > 0 && resumeAt < this.duration) {
      this.sound!.once('play', () => {
        this.sound?.seek(resumeAt);
        this.sliderControl.setValue(resumeAt, { emitEvent: false });
        this.audioPlayerService.updateProgress(resumeAt, this.duration || 0);
      });
      this.startPosition.set(undefined);
    }

    this.sound!.play();
    this.isPlaying = true;
    this.isPlaying$.next(true);
  }

  setTimerWhenDragging($event: MatSliderDragEvent): void {
    if (this.isExternalSource) {
      this.audioPlayerService.sendCommand({ type: 'seek', value: $event.value });
      return;
    }
    this.sound!.seek($event.value);
  }

  step(): void {
    this.ngZone.runOutsideAngular(() => {
      this.setTimer();

      if (!__.IsNullOrUndefined(this.sound)) {
        const pos = (this.sound.seek() as number) || 0;
        this.audioPlayerService.updateProgress(pos, this.duration || 0);
        // Persist our own playhead (service-throttled) so the episode resumes
        // correctly even after the detail page that handed us playback is gone.
        this.persistPosition();

        if (this.sound.playing()) {
          requestAnimationFrame(this.step.bind(this));
        }
      }
    });
  }

  setTimer(value?: number) {
    if (__.IsNullOrUndefined(value)) {
      if (__.IsNullOrUndefined(this.sound)) {
        value = 0;
      } else {
        value = (this.sound.seek() as number) || 0;
      }
    }
    const timer = this.timer();
    if (!timer?.nativeElement) {
      // @ViewChild('timer') is only populated after ngAfterViewInit; a
      // BehaviorSubject replay during construction can call this earlier.
      return;
    }
    timer.nativeElement.innerHTML = this.minuteSecondsPipe.transform(value as number);
  }

  seek(position: number): void {
  }

  /** Chip on the bar: the choice is page-wide, the bar follows through preference$. */
  onLocaleSelected(locale: string): void {
    this.contentLocaleService.setPreference(locale);
  }

  play(): void {
    if (this.isExternalSource) {
      this.audioPlayerService.sendCommand({ type: 'play' });
      return;
    }
    if (this.sound!.playing() === false && this.duration > 0) {
      this.sound!.play();

      if ('mediaSession' in navigator) {
        navigator.mediaSession.playbackState = 'playing';
      }
    }
  }

  pause(): void {
    if (this.isExternalSource) {
      this.audioPlayerService.sendCommand({ type: 'pause' });
      return;
    }
    if (this.sound!.playing() === true && this.duration > 0) {
      this.sound!.pause();

      if ('mediaSession' in navigator) {
        navigator.mediaSession.playbackState = 'paused';
      }
    }
  }

  onEnd(): void {
    if (this.trackListenInterval) {
      clearInterval(this.trackListenInterval);
    }
    this.secondsStreamed = Math.abs(Math.round(this.duration - this.streamStart));
    this.sound!.stop();
    this.sound!.pause();
    this.endReached.emit(this.secondsStreamed);
  }

  /**
   * Persist the playhead for the currently-playing episode. The mini-player is
   * the true source after a video→audio switch, and it keeps playing after the
   * detail page is gone — so it must remember its own position (throttled by the
   * service). `flush` forces an immediate write (pause/teardown).
   */
  private persistPosition(flush = false): void {
    const id = playbackTrackKey(this.currentItem?.episode);
    if (!this.playbackPositionService || __.IsNullOrUndefinedOrEmpty(id) || __.IsNullOrUndefined(this.sound)) {
      return;
    }
    const pos = (this.sound.seek() as number) || 0;
    if (flush) {
      this.playbackPositionService.flush(id, pos, this.duration || 0);
    } else {
      this.playbackPositionService.save(id, pos, this.duration || 0);
    }
  }

  private onPause(): void {
    if (this.trackListenInterval) {
      clearInterval(this.trackListenInterval);
    }

    this.persistPosition(true);
    this.audioPlayerService.pause();

    if (!__.IsNullOrUndefined(this.sound)) {
      this.streamEnd = this.sound.seek();
    }
    if (this.streamStart < this.streamEnd) {
      this.secondsStreamed = Math.abs(Math.round(this.streamEnd - this.streamStart));
    } else {
      this.secondsStreamed = 0;
    }
    this.pauseClicked.emit(this.secondsStreamed);
    this.isPlaying = false;
    this.isPlaying$.next(false);

    if (!__.IsNullOrUndefined(this.currentItem)) {
      this.currentItem.isPlaying = false;
    }

    this.cdr.detectChanges();
  }
}
